import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "../../styles/Capabilities/Capabilities.css";

/* ================= ASSETS ================= */
import cath from "../../assets/capabilities/environmental/cath-chamber.png";
import thermal from "../../assets/capabilities/environmental/thermal-shock.png";
import rain from "../../assets/capabilities/environmental/rain-chamber.png";
import humidity from "../../assets/capabilities/environmental/humidity-chamber.png";
import salt from "../../assets/capabilities/environmental/salt-spray.png";
import temp from "../../assets/capabilities/environmental/temp-chamber.png";

import powervariation from "../../assets/capabilities/electrical/power-variation.png";
import emi from "../../assets/capabilities/emi-emc/emi-setup.png";
import mso from "../../assets/capabilities/MsoAnalyzer/Mso.png";

import gcs from "../../assets/capabilities/dynamic/mgcs-van.png";

import utm from "../../assets/capabilities/measurement/utm.png";
import cmm from "../../assets/capabilities/measurement/cmm.png";
import scanner from "../../assets/capabilities/measurement/optical-scanner.png";

import misc from "../../assets/capabilities/misc/clamp-tester.png";

/* ================================================= */

const Capabilities = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="gov-premium-page">
      {/* ================= HERO ================= */}
      <section className="gov-hero-banner">
        <div className="gov-container">
          <h1>Testing Capabilities</h1>
          <p>State-of-the-art testing infrastructure as per MIL Standards.</p>
        </div>
      </section>

      {/* ================= OFFICIAL INFRASTRUCTURE TABLE ================= */}
      <section className="gov-table-section">
        <div className="gov-container">
          <div className="modern-table-card">
            <div className="gov-table-responsive">
              <table className="official-data-table">
                <thead>
                  <tr>
                    <th className="col-sl">Sl. No.</th>
                    <th>Test</th>
                    <th>Equipment</th>
                  </tr>
                </thead>

                <tbody>
                  {/* ================= ENVIRONMENTAL TEST ================= */}
                  <Group title="Environmental Test" />

                  <TableRow
                    no="1"
                    cat="Altitude Test as per MIL-STD 810"
                    item="Combined Altitude and Humidity Chamber (CATH)"
                  />

                  <TableRow
                    no="2"
                    cat="Low and High Temperature Test as per MIL-STD 810"
                    item="Temperature Chamber"
                  />

                  <TableRow
                    no="3"
                    cat="Humidity Test as per MIL-STD 810"
                    item="Humidity Test Chamber"
                  />

                  <TableRow
                    no="4"
                    cat="Thermal Shock Test as per MIL-STD 810"
                    item="Thermal Shock Test Facility"
                  />

                  <TableRow
                    no="5"
                    cat="Corrosion Test as per MIL-STD 810"
                    item="Salt Spray Chamber"
                  />

                  <TableRow
                    no="6"
                    cat="Rain Test as per IEC 60529"
                    item="Rain Drip Test Chamber"
                  />

                  {/* ================= ELECTRICAL TEST ================= */}
                  <Group title="Electrical Test" />

                  <TableRow
                    no="7"
                    cat="Power Supply Variation Test as per MIL-STD 704 (12 type LDC / 19 type TAC Test)"
                    item="Power Supply Equipment"
                  />

                  <TableRow
                    no="8"
                    cat={
                      <>
                        19 Types of Test (Radiated Emission RE, Radiated
                        Susceptibility RS, Conducted Emission CE and Conducted
                        Susceptibility CS), as MIL-461F
                        <br />
                        High Voltage Test
                      </>
                    }
                    item={
                      <>
                        Semi Anechoic Chamber (SAC)
                        <br />
                        6 EMI / EMC Workstations
                      </>
                    }
                  />

                  <TableRow
                    no="9"
                    cat="Analysis of Data link Navigation & Communication Test"
                    item="Ground Control Station"
                  />

                  <TableRow
                    no="10"
                    cat="Analysis of Data link Navigation & Communication Test for Airborne Unit"
                    item="Mobile Ground Control Station"
                  />

                  {/* ================= STANDARD TESTING & MEASUREMENT ================= */}
                  <Group title="Standard Testing & Measurement" />

                  <TableRow
                    no="11"
                    cat="Geometric Dimensioning & Tolerance Measurement"
                    item="Coordinate Measuring Machine (CMM)"
                  />

                  <TableRow
                    no="12"
                    cat="Destructive Testing: Tensile, Compression, Bending, Shear and Peel Tests"
                    item="Universal Testing Machine"
                  />

                  <TableRow
                    no="13"
                    cat={
                      <>
                        Scan Object of Wide Range Shape and Size
                        <br />
                        Reverse Engineering Task
                        <br />
                        Non-Contact Quality Control
                      </>
                    }
                    item="3D Optical Scanner"
                  />

                  <TableRow
                    no="14"
                    cat="Signal Characteristic Analyser"
                    item="Spectrum Analyser"
                  />

                  <TableRow
                    no="15"
                    cat="Real Time Signal Characteristic Analyser"
                    item="Mixed Signal Oscilloscope (MSO) / DPO"
                  />

                  <TableRow
                    no="16"
                    cat="Insulation Resistance Tests"
                    item="Insulation Tester Megger"
                  />

                  <TableRow
                    no="17"
                    cat="Leakage Current Test"
                    item="Clamp Meter"
                  />

                  <TableRow
                    no="18"
                    cat="Ground Continuity Test"
                    item="Digital Earth Resistance Meter"
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ================= DETAILED SECTIONS ================= */}
      <div className="gov-container main-content">
        <CapabilitySection title="Environmental Testing Facilities">
          <CapabilityCard
            link="/capabilities/cath-chamber"
            img={cath}
            title="Climate Altitude Chamber"
          />
          <CapabilityCard
            link="/capabilities/humidity-chamber"
            img={humidity}
            title="Humidity Chamber"
          />
          <CapabilityCard
            link="/capabilities/temp-chamber"
            img={temp}
            title="Temperature Chamber"
          />
          <CapabilityCard
            link="/capabilities/rain-chamber"
            img={rain}
            title="Rain Drip Test Chamber"
          />
          <CapabilityCard
            link="/capabilities/thermal-shock"
            img={thermal}
            title="Thermal Shock Chamber"
          />
          <CapabilityCard
            link="/capabilities/salt-spray"
            img={salt}
            title="Salt Spray Test Equipment"
          />
        </CapabilitySection>

        <CapabilitySection title="Power Supply Variation Test Setup">
          <CapabilityCard
            link="/capabilities/power-variation"
            img={powervariation}
            title="Power Supply Equipment"
          />
        </CapabilitySection>

        <CapabilitySection title="EMI / EMC Test Setup">
          <CapabilityCard
            link="/capabilities/emi-emc"
            img={emi}
            title="EMI / EMC Test Facility"
          />
        </CapabilitySection>

        <CapabilitySection title="Mobile Ground Control Station">
          <CapabilityCard
            link="/capabilities/gcs"
            img={gcs}
            title="Ground Control Station (GCS)"
          />
        </CapabilitySection>

        <CapabilitySection title="Universal Testing Machine">
          <CapabilityCard
            link="/capabilities/utm"
            img={utm}
            title="Universal Testing Machine (UTM)"
          />
        </CapabilitySection>

        <CapabilitySection title="Standard Testing & Measurement">
          <CapabilityCard
            link="/capabilities/cmm"
            img={cmm}
            title="Coordinate Measuring Machine (CMM)"
          />
          <CapabilityCard
            link="/capabilities/optical-scanner"
            img={scanner}
            title="3D Optical Scanner"
          />
          <CapabilityCard
            link="/capabilities/misc"
            img={misc}
            title="Electrical & Support Instruments"
          />
          <CapabilityCard
            link="/capabilities/emi-instrumentation"
            img={mso}
            title="MSO & Spectrum Analyzer"
          />
        </CapabilitySection>
      </div>
    </div>
  );
};

/* ================= HELPERS ================= */

const Group = ({ title }) => (
  <tr className="table-sub-group">
    <td colSpan="3">{title}</td>
  </tr>
);

const TableRow = ({ no, cat, item }) => (
  <tr>
    <td>{no}</td>
    <td>{cat}</td>
    <td>{item}</td>
  </tr>
);

const CapabilitySection = ({ title, children }) => (
  <div className="gov-section-group">
    <h3 className="group-heading">{title}</h3>
    <div className="gov-grid-layout">{children}</div>
  </div>
);

const CapabilityCard = ({ link, img, title }) => (
  <Link to={link} className="modern-fac-card">
    <div className="img-container">
      <img src={img} alt={title} />
    </div>
    <div className="card-details">
      <h4>{title}</h4>
      <div className="card-footer">
        <span>Detailed Specs</span>
        <span className="arrow-icon">→</span>
      </div>
    </div>
  </Link>
);

export default Capabilities;
